import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Snowflake, Lock, Eye, EyeOff, CheckCircle, XCircle, 
  Shield, AlertTriangle, Loader2, Database, Server, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple encryption for localStorage (not for production secrets, just obfuscation)
const encryptCredentials = (creds) => {
  return btoa(JSON.stringify(creds));
};

const decryptCredentials = (encrypted) => {
  try {
    return JSON.parse(atob(encrypted));
  } catch {
    return null;
  }
};

const SnowflakeConnect = ({ isOpen, onClose, onConnected }) => {
  const [credentials, setCredentials] = useState({
    account: '',
    username: '',
    password: '',
    warehouse: '',
    role: 'ACCOUNTADMIN'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [rememberCredentials, setRememberCredentials] = useState(false);

  // Load saved credentials on mount
  useEffect(() => {
    const saved = localStorage.getItem('snowflake_creds');
    if (saved) {
      const decrypted = decryptCredentials(saved);
      if (decrypted) {
        setCredentials(decrypted);
        setRememberCredentials(true);
      }
    }
  }, []);

  const handleConnect = async () => {
    if (!credentials.account || !credentials.username || !credentials.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsConnecting(true);
    setConnectionStatus(null);

    try {
      const response = await axios.post(`${API_URL}/api/snowflake/connect`, credentials);
      
      if (response.data.success) {
        setConnectionStatus({
          success: true,
          ...response.data.account_info
        });

        // Save to localStorage if requested (encrypted)
        if (rememberCredentials) {
          localStorage.setItem('snowflake_creds', encryptCredentials(credentials));
        } else {
          localStorage.removeItem('snowflake_creds');
        }

        // Store credentials in sessionStorage for this session only
        sessionStorage.setItem('snowflake_session', encryptCredentials(credentials));

        toast.success('Connected to Snowflake successfully!');
        
        if (onConnected) {
          onConnected(credentials, response.data.account_info);
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Connection failed';
      setConnectionStatus({
        success: false,
        error: message
      });
      toast.error(message);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleClearSaved = () => {
    localStorage.removeItem('snowflake_creds');
    sessionStorage.removeItem('snowflake_session');
    setCredentials({
      account: '',
      username: '',
      password: '',
      warehouse: '',
      role: 'ACCOUNTADMIN'
    });
    setRememberCredentials(false);
    setConnectionStatus(null);
    toast.success('Saved credentials cleared');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Snowflake className="w-5 h-5 text-cyan-500" />
            Connect to Snowflake
          </DialogTitle>
          <DialogDescription>
            Enter your Snowflake credentials to view real-time cost data.
          </DialogDescription>
        </DialogHeader>

        {/* Security Notice */}
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <Shield className="w-5 h-5 text-emerald-500 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                Your data stays secure
              </p>
              <p className="text-xs text-emerald-600 dark:text-emerald-400">
                Credentials are stored only in your browser. We query your Snowflake directly - 
                <strong> no data is ever stored on our servers.</strong>
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 py-2">
          {/* Account */}
          <div className="space-y-2">
            <Label htmlFor="account">Account Identifier *</Label>
            <Input
              id="account"
              placeholder="abc12345.us-east-1 or abc12345"
              value={credentials.account}
              onChange={(e) => setCredentials({ ...credentials, account: e.target.value })}
            />
            <p className="text-xs text-slate-400">
              Found in your Snowflake URL: https://[account].snowflakecomputing.com
            </p>
          </div>

          {/* Username */}
          <div className="space-y-2">
            <Label htmlFor="username">Username *</Label>
            <Input
              id="username"
              placeholder="your_username"
              value={credentials.username}
              onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
            />
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="password">Password *</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-400"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Warehouse */}
          <div className="space-y-2">
            <Label htmlFor="warehouse">Warehouse (optional)</Label>
            <Input
              id="warehouse"
              placeholder="COMPUTE_WH"
              value={credentials.warehouse}
              onChange={(e) => setCredentials({ ...credentials, warehouse: e.target.value })}
            />
          </div>

          {/* Role */}
          <div className="space-y-2">
            <Label htmlFor="role">Role</Label>
            <Input
              id="role"
              placeholder="ACCOUNTADMIN"
              value={credentials.role}
              onChange={(e) => setCredentials({ ...credentials, role: e.target.value })}
            />
            <p className="text-xs text-slate-400">
              Requires access to SNOWFLAKE.ACCOUNT_USAGE schema
            </p>
          </div>

          {/* Remember Credentials */}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberCredentials}
                onChange={(e) => setRememberCredentials(e.target.checked)}
                className="rounded border-slate-300"
              />
              <span className="text-sm text-slate-400 dark:text-slate-400">
                Remember credentials (browser only)
              </span>
            </label>
            {localStorage.getItem('snowflake_creds') && (
              <Button variant="ghost" size="sm" onClick={handleClearSaved} className="text-red-500">
                <Trash2 className="w-4 h-4 mr-1" />
                Clear saved
              </Button>
            )}
          </div>

          {/* Connection Status */}
          {connectionStatus && (
            <div className={`p-3 rounded-lg border ${
              connectionStatus.success 
                ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800'
                : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
            }`}>
              <div className="flex items-start gap-2">
                {connectionStatus.success ? (
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                <div>
                  <p className={`text-sm font-medium ${
                    connectionStatus.success ? 'text-emerald-700' : 'text-red-700'
                  }`}>
                    {connectionStatus.success ? 'Connected Successfully' : 'Connection Failed'}
                  </p>
                  {connectionStatus.success ? (
                    <div className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 space-y-0.5">
                      <p>Account: {connectionStatus.account}</p>
                      <p>User: {connectionStatus.user}</p>
                      <p>Role: {connectionStatus.role}</p>
                      <p>Warehouse: {connectionStatus.warehouse || 'Default'}</p>
                    </div>
                  ) : (
                    <p className="text-xs text-red-600">{connectionStatus.error}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleConnect} 
            disabled={isConnecting}
            className="bg-cyan-500 hover:bg-cyan-600"
          >
            {isConnecting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <Database className="w-4 h-4 mr-2" />
                Connect
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Hook to get current Snowflake credentials from session
export const useSnowflakeCredentials = () => {
  const [credentials, setCredentials] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // Check session storage first
    const sessionCreds = sessionStorage.getItem('snowflake_session');
    if (sessionCreds) {
      const decrypted = decryptCredentials(sessionCreds);
      if (decrypted) {
        setCredentials(decrypted);
        setIsConnected(true);
        return;
      }
    }
    
    // Fall back to localStorage
    const savedCreds = localStorage.getItem('snowflake_creds');
    if (savedCreds) {
      const decrypted = decryptCredentials(savedCreds);
      if (decrypted) {
        setCredentials(decrypted);
        setIsConnected(true);
      }
    }
  }, []);

  const disconnect = () => {
    sessionStorage.removeItem('snowflake_session');
    setCredentials(null);
    setIsConnected(false);
  };

  const updateCredentials = (newCreds) => {
    setCredentials(newCreds);
    setIsConnected(true);
    sessionStorage.setItem('snowflake_session', encryptCredentials(newCreds));
  };

  return { credentials, isConnected, disconnect, updateCredentials };
};

export default SnowflakeConnect;
